import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { MtxGridColumn } from '@ng-matero/extensions/grid';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { firstValueFrom, take } from 'rxjs';
import { CavoRaccordo } from '../../../../models/cavo-raccordo.model';
import { RaccordoService } from '../../../../services/raccordo.service';

@Component({
  selector: 'ebw-monorepo-raccordi-cavo-attachments',
  templateUrl: './raccordi-cavo-attachments.component.html',
  styleUrls: ['./raccordi-cavo-attachments.component.scss']
})
export class RaccordiCavoAttachmentsComponent implements OnInit {
  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate!: TemplateRef<any>;

  public isEditable = true;
  public raccordo: CavoRaccordo;
  public selectedFile: File | null = null;
  public attachments = [];
  public columnGrid: MtxGridColumn[] = [];

  constructor(
    public dialogRef: MatDialogRef<any>,
    private _raccordiService: RaccordoService,
    private _ebwModal: EbwModalService,
    private _translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.isEditable = this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_EDIT_WEB);

    this._initAttachmentsTable();
    this._fetchData();
  }

  public uploadFile() {
    const formData = new FormData();
    formData.append('id', this.raccordo.id.toString());

    if (this.selectedFile) {
      formData.append('file', this.selectedFile, this.selectedFile.name);
    }

    this._raccordiService.uploadRaccordoFile(formData).subscribe({
      next: (res) => {
        const successTitle = this._translateService.instant('common.save_successful');
        this._ebwModal.openSuccessModal({ title: successTitle, content: '' });

        this._fetchData();
      },
      error: (error) => {
        const errorTitle = this._translateService.instant('common.error');
        this._ebwModal.openErrorModal({ title: errorTitle, content: error });
      }
    });
  }

  public onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      this.selectedFile = file;
    }
  }

  public deleteFile(input: HTMLInputElement) {
    this.selectedFile = null;
    input.value = null;
  }

  public onDeleteAttachment(id) {
    let confirmDeleteModal = this._ebwModal.openConfirmModal({
      title: this._translateService.instant('common.delete_title'),
      content: this._translateService.instant('map.sidebar.raccordo-cavo.attachment.delete_confirm')
    });

    confirmDeleteModal.afterClosed().subscribe((result) => {
      if (result) {
        this._raccordiService.deleteRaccordoFile(id).subscribe({
          next: (res) => {
            const successTitle = this._translateService.instant('common.delete_success');
            this._ebwModal.openSuccessModal({ title: successTitle, content: '' });

            this._fetchData();
          },
          error: (error) => {
            const errorTitle = this._translateService.instant('common.error');
            this._ebwModal.openErrorModal({ title: errorTitle, content: error });
          }
        });
      }
    });
  }

  public download(rowData) {
    this._raccordiService
      .downloadRaccordoFile(rowData.url)
      .pipe(take(1))
      .subscribe({
        next: (url: any) => {
          const a = document.createElement('a');
          a.href = url;
          a.download = `${rowData['name']}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        },
        error: (error: any) => {
          this._ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: error.message != '' ? error.message : ''
          });
        }
      });
  }

  private async _getLatestRaccordo(id) {
    try {
      const result = await firstValueFrom(this._raccordiService.getRaccordo(id));

      return safeJsonClone(result);
    } catch (error) {
      return null;
    }
  }

  private async _fetchData() {
    const raccordoTmp: CavoRaccordo = await this._getLatestRaccordo(this.data.id);

    if (!raccordoTmp) {
      const errorTitle = this._translateService.instant('common.error');
      const errorContent = this._translateService.instant('common.data_retrieving_error');
      this._ebwModal.openErrorModal({ title: errorTitle, content: errorContent });

      return;
    }

    this.raccordo = raccordoTmp;
    this.attachments = [...this.raccordo.allegati];
  }

  private _initAttachmentsTable() {
    this.columnGrid = [
      {
        header: 'Name',
        field: 'name'
      },
      {
        header: 'Date',
        field: 'timestamp',
        formatter: (data: any) => `<span class="date-field">${moment(data.timestamp).format('DD/MM/YYYY HH:mm')}</span>`
      },
      {
        header: 'User',
        field: 'user'
      },
      {
        header: '',
        field: 'id',
        cellTemplate: this.actionsTemplate,
        width: '70px'
      }
    ];
  }
}
